import { Box, Button, ButtonProps, Icon, SxProps } from "@outschool/backpack";
import { faHeart, fasHeart } from "@outschool/icons";
import React from "react";
import { animated, useTransition } from "react-spring";

export type HeartButtonProps = {
  isSet: boolean;
  setLabel?: any;
  unsetLabel?: any;
  onChange: any;
  simpleHeart?: boolean;
  variant?: ButtonProps["variant"];
  size?: ButtonProps["size"];
  sx?: SxProps;
  disabled?: boolean;
};

const HeartButton = React.forwardRef<HTMLElement, HeartButtonProps>(
  function HeartButton(
    {
      simpleHeart = false,
      isSet,
      variant,
      setLabel,
      unsetLabel,
      onChange,
      size,
      disabled,
      sx,
    },
    ref,
  ) {
    const onChangeCallback = React.useCallback(() => {
      return onChange(!isSet);
    }, [onChange, isSet]);
    const onClick = (e: any) => {
      e.preventDefault();
      if (disabled) {
        return;
      }
      e.stopPropagation();
      onChangeCallback();
    };

    return (
      <Button
        variant={simpleHeart ? "link" : variant || "outlined"}
        ref={ref as any}
        size={size}
        onClick={onClick}
        sx={{ ...sx }}
        aria-label={isSet ? setLabel : unsetLabel}
        data-test-id="save-button"
        startIcon={simpleHeart ? undefined : <HeartIcon isSet={isSet} />}
      >
        {simpleHeart ? (
          <HeartIcon isSet={isSet} />
        ) : (
          <>{isSet ? setLabel : unsetLabel}</>
        )}
      </Button>
    );
  },
);

const AnimatedIcon = animated(Icon);

interface HeartIconProps {
  isSet: boolean;
}

function HeartIcon({ isSet }: HeartIconProps) {
  const transitions = useTransition(isSet, null, {
    config: { mass: 3, tension: 500, friction: 30 },
    immediate: !isSet,
    from: { transform: "scale(0)" },
    enter: { transform: "scale(1)" },
    leave: { transform: "scale(0)" },
  });

  return (
    <Box
      flex
      sx={[
        {
          position: "relative",
        },
      ]}
    >
      <Icon
        icon={fasHeart}
        sx={{
          visibility: "hidden",
        }}
      />
      {transitions.map(({ item: isSet, key, props: style }) => (
        <React.Fragment key={key}>
          <AnimatedIcon
            key={key}
            icon={isSet ? fasHeart : faHeart}
            sx={{
              position: "absolute",
              left: 0,
              color: "error.main",
            }}
            style={style}
          />
        </React.Fragment>
      ))}
    </Box>
  );
}

export default HeartButton;
